/**
 * What the wizard and the out-of-credits dialog send billing: the order a
 * selection becomes, and the checkout and preview requests that carry it.
 * Pure: no React, no network.
 */
import type {
  CreateCheckoutSessionRequest,
  PreviewPlanChangeRequest,
} from '@/store/features/billing.feature';
import type { AvailablePlans, Plan, TrialState } from '@/types';
import type { UpgradeInterval, UpgradeTier } from '@/lib/upgrade-link';
import {
  addonOption,
  dropsUnpricedSenders,
  isCardTrial,
  planForTier,
  quoteSelection,
  resolveSelection,
  type UpgradeQuote,
  type UpgradeSelection,
} from './upgrade-model';

/** Everything checkout needs, for a selection that can be bought. */
export interface UpgradeOrder {
  planId: string;
  planName: string;
  tier: UpgradeTier;
  interval: UpgradeInterval;
  priceId: string;
  seats: number;
  /** The order's price for the interval, in dollars. */
  total: number | null;
  /** Workspace credits a month. */
  credits: number;
  addonPriceId?: string;
  addonCredits?: number;
  /** Pro's extra senders, 0 included: billing keeps a subscription's own
   *  when the count is left out, so a Pro order always names it. Absent on
   *  Starter, which has none. */
  senders?: number;
  /** With senders above 0: their price for the interval. */
  senderPriceId?: string;
}

export function buildOrder(
  plan: Plan,
  selection: UpgradeSelection,
  quote: UpgradeQuote
): UpgradeOrder | null {
  if (!quote.available || !quote.priceId) return null;
  return {
    planId: plan.id,
    planName: plan.name,
    tier: selection.tier,
    interval: selection.interval,
    priceId: quote.priceId,
    seats: quote.seats,
    total: quote.total,
    credits: quote.credits,
    ...(quote.addonPriceId
      ? { addonPriceId: quote.addonPriceId, addonCredits: quote.addonCredits }
      : {}),
    ...(selection.tier === 'pro' ? { senders: quote.senders } : {}),
    ...(quote.senderPriceId ? { senderPriceId: quote.senderPriceId } : {}),
  };
}

/**
 * Identifies an order's billing terms, so a preview answers one order. A
 * count left out (Starter) and an explicit 0 are different terms. `basis` is
 * what the order changes (see previewBasis): the same terms against a
 * subscription that has since moved are a different question.
 */
export const orderKey = (order: UpgradeOrder, basis = '') =>
  [
    order.priceId,
    order.seats,
    order.addonPriceId ?? '',
    order.addonCredits ?? 0,
    order.senders ?? '',
    order.senderPriceId ?? '',
    basis,
  ]
    .map(String)
    .join('|');

/**
 * What a preview of an order also depends on: the subscription and trial it
 * changes. A webhook or a refetch that moves either makes an earlier
 * preview another order's.
 */
export function previewBasis(
  catalog: AvailablePlans,
  trial: TrialState | null
): string {
  const current = catalog.current_plan;
  return JSON.stringify([
    current?.id ?? null,
    catalog.current_billing_interval ?? null,
    catalog.seats_paid ?? null,
    current?.credit_addon_credits ?? null,
    current?.sender_addon_quantity ?? null,
    trial?.ends_at ?? null,
    trial?.renews_to ?? null,
  ]);
}

/**
 * - `checkout`: a new subscription (Stripe Checkout) or a confirmed change to
 *   the current one.
 * - `start_now`: a card-backed trial ends today and the plan is charged.
 * - `renewal`: a card-backed trial keeps running; only the plan it becomes
 *   changes, and nothing is charged.
 */
export type PayMode = 'checkout' | 'start_now' | 'renewal';

/**
 * The order's add-ons as billing names them. Extra credits go only when the
 * order has them: checkout sends the whole subscription, so leaving them out
 * drops them. Extra senders are the other way round: billing keeps the
 * subscription's own when their count is left out, so a Pro order always
 * names it, 0 to remove them, with their price above 0. Starter sends
 * neither.
 */
function addonFields(order: UpgradeOrder) {
  return {
    ...(order.addonPriceId
      ? {
          credit_addon_price_id: order.addonPriceId,
          credit_addon_credits: order.addonCredits,
        }
      : {}),
    ...(order.senders !== undefined
      ? { sender_addon_quantity: order.senders }
      : {}),
    ...(order.senderPriceId
      ? { sender_addon_price_id: order.senderPriceId }
      : {}),
  };
}

export function checkoutRequest(
  order: UpgradeOrder,
  mode: PayMode
): Omit<CreateCheckoutSessionRequest, 'organizationId'> {
  return {
    price_id: order.priceId,
    seats: order.seats,
    ...(mode === 'start_now' ? { start_now: true } : {}),
    ...addonFields(order),
  };
}

export function previewRequest(
  order: UpgradeOrder
): Omit<PreviewPlanChangeRequest, 'organizationId'> {
  return {
    price_id: order.priceId,
    seats: order.seats,
    ...addonFields(order),
  };
}

/**
 * The order that starts a card trial today on a tier: what the trial renews
 * to (its interval, seats and Pro add-ons), as the wizard opens on that tier.
 * Null when the catalog can't sell it as it stands.
 */
export function startNowOrder(
  catalog: AvailablePlans,
  trial: TrialState | null,
  tier: UpgradeTier
): UpgradeOrder | null {
  const plan = planForTier(catalog, tier);
  if (!plan || !isCardTrial(trial)) return null;
  const selection = resolveSelection(catalog, trial, { plan: tier });
  if (
    selection.tier !== tier ||
    dropsUnpricedSenders(catalog, trial, selection)
  ) {
    return null;
  }
  return orderFor(plan, selection);
}

/** The selection's order on its plan, quoted from the catalog. */
export function orderFor(
  plan: Plan,
  selection: UpgradeSelection
): UpgradeOrder | null {
  const quote: UpgradeQuote = quoteSelection(
    plan,
    selection.interval,
    selection.seats,
    addonOption(plan, selection.addonCredits),
    selection.senders
  );
  return buildOrder(plan, selection, quote);
}

/** A charge preview is only good for confirming a charge when it names one. */
export const isCharge = (amount: unknown): amount is number =>
  typeof amount === 'number' && Number.isFinite(amount) && amount >= 0;
